/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import React, { useState, useEffect } from "react";
import {
	TextField,
	Checkbox,
	ListItemText,
	ListItemSecondaryAction,
	IconButton,
	ListItem,
	Grid,
} from "@material-ui/core";
import { format, isValid, startOfDay } from "date-fns";
import { KeyboardDatePicker } from "@material-ui/pickers";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import { Delete, Edit } from "@material-ui/icons";
import { ActionButtons } from "../../../../components/Common";

const getDate = (date, minified) => {
	return minified ? format(date, "MM/dd") : format(date, "MM/dd/yyyy");
};

/**
 * This component is used for displaying nad potentially editing a holiday.
 * @param {*} holiday The holiday to be displayed.
 * @param {*} editHoliday The callback method to be called when the holiday is edited.
 * @param {*} deleteHoliday The callback method to be called when the holiday is deleted.
 * @param {*} holidaysToDisplay The list of all the holidays so that when editing we dont add 2 holidays with the same
 */
export const HolidayCard = ({
	holiday,
	editHoliday,
	deleteHoliday,
	holidaysToDisplay,
	setIsLoading,
	setItemModified,
}) => {
	const [startDate, setStartDate] = useState(new Date(holiday.startDate));
	const [endDate, setEndDate] = useState(new Date(holiday.endDate));
	const [name, setName] = useState(holiday.holidayName);
	const [isRepeated, setRepeated] = useState(holiday.expireTime === undefined);
	const [editMode, setEditMode] = useState(false);
	const [formEdited, setFormEdited] = useState(false);
	const [errors, setErrors] = useState({ startDate: !!startDate, endDate: !!endDate, holidayName: !!name });

	useEffect(() => {
		const isModified =
			holiday.startDate !== getDate(startDate, isRepeated) ||
			holiday.endDate !== getDate(endDate, isRepeated) ||
			name !== holiday.holidayName ||
			isRepeated !== (holiday.expireTime === undefined);
		setFormEdited(isModified);
		setItemModified(isModified, holiday.holidayName);
	}, [holiday, startDate, endDate, name, isRepeated, setItemModified]);

	useEffect(() => {
		const holidayNameError =
			name.length === 0 ||
			(holidaysToDisplay && holidaysToDisplay.filter((holiday) => holiday.holidayName === name).length !== 0);
		setErrors((err) => ({ ...err, holidayName: holidayNameError }));
	}, [name, setErrors, holidaysToDisplay]);

	const setNewEndDate = (date) => {
		const currentYear = new Date().getFullYear();
		let newEndDate = date;
		if (isValid(newEndDate) && isRepeated && newEndDate < startDate) {
			newEndDate.setFullYear(currentYear + 1);
		}
		setEndDate(newEndDate);
	};
	const formatString = isRepeated ? "MM/dd" : "MM/dd/yyyy";

	const startOfDayDate = startOfDay(new Date());

	const saveHolidayButtonDisabled =
		errors.startDate ||
		errors.endDate ||
		errors.holidayName ||
		startDate > endDate ||
		(startDate < startOfDayDate && !isRepeated);

	const handleDatePickerError = (field, error) => {
		if (!!error !== errors[field]) {
			setErrors((err) => ({ ...err, [field]: !!error }));
		}
	};

	return (
		<ListItem>
			{editMode ? (
				<Grid container justifyContent="space-between" spacing={2} className="mt-3">
					<Grid item xs={12}>
						<ActionButtons
							showCancel
							showRemove
							showSave={formEdited}
							onCancel={() => {
								setEndDate(new Date(holiday.endDate));
								setStartDate(new Date(holiday.startDate));
								setName(holiday.holidayName);
								setRepeated(holiday.expireTime === undefined);
								setEditMode(false);
								setFormEdited(false);
								setItemModified(false, holiday.holidayName);
							}}
							onRemove={deleteHoliday}
							onSave={async () => {
								setIsLoading(true);
								setItemModified(false, holiday.holidayName);
								await editHoliday(name, startDate, endDate, isRepeated);
								setIsLoading(false);
								setEditMode(false);
							}}
							disableSave={saveHolidayButtonDisabled}
							className="float-right"
						/>
					</Grid>
					<Grid item xs={12} md={6}>
						<TextField
							className="w-100"
							id="standard-basic"
							label="Holiday Name"
							value={name}
							onChange={(event) => {
								setName(event.target.value);
							}}
							error={errors.holidayName}
							helperText={errors.holidayName ? "A unique holiday name is required." : ""}
						/>
					</Grid>
					<Grid item xs={12} md={6} className="mt-2">
						<FormControlLabel
							className="w-100 m-0"
							value="top"
							control={
								<Checkbox
									onChange={(event) => {
										setRepeated(event.target.checked);
										if (isRepeated) {
											let newStartDate = startDate;
											let newEndDate = endDate;
											const currentYear = new Date().getFullYear();
											if (currentYear > newStartDate.getFullYear()) {
												newStartDate.setFullYear(currentYear);
												setStartDate(newStartDate);
											}
											newEndDate.setFullYear(currentYear);
											if (newStartDate > newEndDate) {
												newEndDate.setFullYear(currentYear + 1);
											}
											setEndDate(newEndDate);
										}
									}}
									checked={isRepeated}
								/>
							}
							label="Repeat"
						/>
					</Grid>
					<Grid item xs={12} md={6} className="mt-2">
						<KeyboardDatePicker
							disableToolbar
							variant="inline"
							format={formatString}
							margin="normal"
							id="start-date-picker-inline"
							label="Start Date"
							value={startDate}
							onChange={(val) => {
								setStartDate(val);
							}}
							onError={(err) => handleDatePickerError("startDate", err)}
							minDate={isRepeated ? null : startOfDayDate}
							minDateMessage="Date cannot be in the past"
							className="w-100 m-0"
							KeyboardButtonProps={{
								"aria-label": "change date",
							}}
						/>
					</Grid>
					<Grid item xs={12} md={6} className="mt-2">
						<KeyboardDatePicker
							disableToolbar
							variant="inline"
							format={formatString}
							margin="normal"
							id="end-date-picker-inline"
							label="End Date"
							minDate={isRepeated ? undefined : startDate}
							minDateMessage={"End Date Cannot come before the Start Date"}
							value={endDate}
							onChange={(val) => {
								setNewEndDate(val);
							}}
							onError={(err) => handleDatePickerError("endDate", err)}
							className="w-100 m-0"
							KeyboardButtonProps={{
								"aria-label": "change date",
							}}
						/>
					</Grid>
				</Grid>
			) : (
				<>
					<ListItemText primary={holiday.holidayName} secondary={`Start Date: ${holiday.startDate}`} />
					<ListItemText className="mt-auto" primary=" " secondary={`End Date: ${holiday.endDate}`} />
					<ListItemSecondaryAction>
						<IconButton edge="end" aria-label="delete" onClick={deleteHoliday}>
							<Delete />
						</IconButton>
						<IconButton edge="end" aria-label="edit" onClick={() => setEditMode(true)}>
							<Edit />
						</IconButton>
					</ListItemSecondaryAction>
				</>
			)}
		</ListItem>
	);
};
